import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Only populated in edit mode (hydrated from the fetched work order). The header
  // reads this to offer "Review & Publish" on a customer-raised PENDING_APPROVAL
  // ticket instead of a plain "Update".
  status: null,
  summary: {
    overview: {
      templateId: null,
      title: "",
      serviceLevelAgreeement: "",
      clientId: null,
      projectId: null,
      teamId: null,
      secondaryTeamId: null,
      isExportDisable: false,
      isCounterOffersDisable: false,
      isGpsRequire: false,
      incidentId: "",
    },
    serviceDescription: {
      description: "",
      confidentialInformation: "",
    },
    workExperience: {
      categoryId: null,
      vendorId: null,
      workOrderServices: [],
    },
    customerFiles: {
      isCustomerFilesVisible: false,
      files: [],
    },
    // Ids of the documents the work order already had when the edit wizard
    // hydrated. Saving diffs the current list against this to know what the user
    // removed — the API only ever attaches ids, it never detaches them.
    customerFilesBaseline: [],
    deliverables: {
      files: [],
    },
  },
  scheduling: {
    schedule: {
      scheduleType: "HARD_START",
      orderStartDate: "",
      orderStartTime: "",
      orderEndDate: null,
      orderEndTime: null,
      timeZone: "",
    },
    location: {
      addressId: "",
      displayName: "",
      locationType: "RESIDENTIAL",
      country: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      longitude: "",
      latitude: "",
      saveForFuture: false,
      isRemote: false,
      type: "EXACT_LOCATION",
    },
    contacts: {
      workOrderManagerId: "",
      contacts: [],
    },
    qualifications: [
      {
        id: Date.now(),
        qualificationTypeId: null,
        qualificationSubTypes: [],
      },
    ],
  },
  payment: {
    pay: {
      paymentTerms: "15 days",
      paymentType: "HOURLY",
      hourlyRate: null,
      additionalHourlyRate: null,
      minimumHours: null,
      fixedAmount: null,
      halfDayRate: null,
      fullDayRate: null,
      fthRate: null,
      customHours: null,
      customHoursRate: null,
      currency: null,
      nonBusinessHourRate: null,
      purchaseOrderNumber: "",
      purchaseOrder: {
        files: null,
      },
      purchaseOrders: [],
      enableClientAdditionalHourRate: false,
      enableNonBusinessHourRate: false,

      engineerPaymentType: null,
      engineerHourlyRate: null,
      engineerAdditionalHourlyRate: null,
      engineerMinimumHours: null,
      engineerFixedAmount: null,
      engineerHalfDayRate: null,
      engineerFullDayRate: null,
      engineerFthRate: null,
      engineerCurrency: null,
      enableCustomRates: false,
      enableEngineerAdditionalHourRate: false,
    },

    expenses: [],
    engineerExpenses: [],
    companyExpenses: [],

    tasks: [
      {
        step: "Prep",
        taskType: "START_TIME",
        emailsToNotify: [],
        title: "Set start time",
      },
      {
        taskType: "ON_THE_WAY",
        emailsToNotify: [],
        title: "On the Way",
      },
      {
        taskType: "RUNNING_LATE",
        emailsToNotify: [],
        title: "Running Late",
      },
      {
        taskType: "MARK_COMPLETE",
        emailsToNotify: [],
        title: "Mark Complete",
      },

      {
        step: "On Site",

        taskType: "CHECK_IN",
        emailsToNotify: [],
        title: "Check In",
      },
      {
        taskType: "CHECK_OUT",
        emailsToNotify: [],
        title: "Check Out",
      },
      {
        step: "Post",

        taskType: "CLOSE_NOTES",
        emailsToNotify: [],
        title: "Enter closeout notes",
      },
    ],
  },
  workOrderFormValidation: {
    title: false,
    clientId: false,
    projectId: false,
    qualificationTypeId: false,
    qualificationSubTypes: false,
    orderStartDate: false,
    orderStartTime: false,
    addressLine1: false,
    addressId: false,
    hourlyRateErrorMsg: "",
    minimumHours: false,
    categoryId: false,
    vendorId: false,
    workOrderServices: false,
    halfDayRate: false,
    halfDayRateErrorMsg: "",
    fthRateErrorMsg: "",
    fullDayRate: false,
    fullDayRateErrorMsg: "",
    nonBusinessHourRateError: "",
    customHoursErrorMsg: "",
    customHoursRateErrorMsg: "",
    additionalHourlyRateError: "",
    engineerHourlyRateErrorMsg: "",
    engineerMinimumHours: false,
    engineerHalfDayRate: false,
    engineerHalfDayRateErrorMsg: "",
    engineerFthRateErrorMsg: "",
    engineerFullDayRate: false,
    engineerFullDayRateErrorMsg: "",
    engineerFixedAmountError: false,
    engineerFixedAmountErrorMsg: "",
    engineerAdditionalHourlyRateError: "",
  },
  workOrderDetails: {},
  subQualificationOptions: {},
  draftMessages: {},
};

const workOrderSlice = createSlice({
  name: "workOrderSlice",
  initialState,
  reducers: {
    updateWorkOrderForm: (state, action) => {
      const { step, data } = action.payload;

      // Ensure that the state for the given step exists
      if (state[step]) {
        // Iterate through each key in the data object
        Object.keys(data).forEach((key) => {
          if (Array.isArray(data[key])) {
            state[step][key] = data[key];
          } else if (state[step][key]) {
            state[step][key] = {
              ...state[step][key],
              ...data[key],
            };
          } else {
            // If the key is not a nested object, update it directly
            state[step] = {
              ...state[step],
              ...data,
            };
          }
        });
      }
    },
    resetPaymentFieldsAndValidation(state) {
      state.payment.pay = {
        ...state.payment.pay,
        hourlyRate: null,
        minimumHours: null,
        fixedAmount: null,
        halfDayRate: null,
        fullDayRate: null,
      };
      state.workOrderFormValidation = {
        hourlyRate: false,
        hourlyRateErrorMsg: "",
      };
    },
    resetEngineerPaymentFieldsAndValidation(state) {
      state.payment.pay = {
        ...state.payment.pay,
        engineerHourlyRate: null,
        engineerMinimumHours: null,
        engineerFixedAmount: null,
        engineerHalfDayRate: null,
        engineerFullDayRate: null,
        engineerFthRate: null,
      };
      state.workOrderFormValidation = {
        engineerHourlyRate: false,
        engineerHourlyRateErrorMsg: "",
      };
    },

    updateWorkOrderFormValidation: (state, action) => {
      const { fieldName, data } = action.payload;
      state.workOrderFormValidation[fieldName] = data;
    },
    setWorkOrderDetail: (state, action) => {
      state.workOrderDetails = {
        ...state.workOrderDetails,
        ...action.payload,
      };
    },
    clearWorkOrderDetail: (state) => {
      state.workOrderDetails = {};
    },
    updateWorkOrderDetail: (state, action) => {
      state.status = action.payload.status ?? null;
      // Merge rather than replace: the edit-mode hydration only builds a subset of
      // each section (e.g. payment has no `tasks`), and clobbering the section drops
      // the initialState defaults, leaving `undefined` where the form expects [].
      state.summary = { ...state.summary, ...action.payload.summary };
      state.payment = {
        ...state.payment,
        ...action.payload.payment,
        pay: { ...state.payment.pay, ...action.payload.payment?.pay },
        expenses: action.payload.payment?.expenses ?? state.payment.expenses,
      };
      state.scheduling = { ...state.scheduling, ...action.payload.scheduling };
    },
    resetWorkOrderForm: () => initialState, // Reset state to initialState
    addNotification: (state) => {
      state.payment.tasks.push({
        id: Date.now(),
        taskType: "",
        emailsToNotify: [],
      });
    },
    removeNotification: (state, action) => {
      const { taskId, index } = action.payload;

      const task = state.payment.tasks.find((t) => t.id === taskId);
      if (task) {
        task.emailsToNotify.splice(index, 1);
      }
    },

    addEmailToNotify: (state, action) => {
      const { taskId } = action.payload;

      const filteredTask = state.payment.tasks.find((task) => task.id === taskId);

      if (filteredTask) {
        filteredTask.emailsToNotify.push({ id: Date.now(), email: "" });
      }
    },
    updateEmail: (state, action) => {
      const { taskId, id, email } = action.payload;

      const task = state.payment.tasks.find((task) => task.id === taskId);
      if (!task) return;

      task.emailsToNotify = task.emailsToNotify.map((item, index) => {
        if (typeof item === "string") {
          if (index === id) return email;
          return item;
        }

        if (item.id === id) {
          return { ...item, email };
        }

        return item;
      });
    },
    taskHandleChange: (state, action) => {
      const { id, value, field } = action.payload;

      // Find the task by its ID
      const task = state.payment?.tasks?.find((task) => task.id === id);
      if (task) {
        // Check field and update the corresponding task field
        switch (field) {
          case "phone":
            task.phone = value;
            break;
          case "reason":
            task.reason = value;
            break;
          case "signatureFrom":
            task.signatureFrom = value;
            break;
          case "description":
            task.description = value;
            break;
          case "title":
            console.log("i am here");
            task.title = value;
            break;
          default:
            break;
        }
      }
    },
    addCustomTask: (state, action) => {
      const { step } = action.payload;

      // Create a custom task
      const newTask = {
        id: new Date().getTime(),
        step: "On Site",
        taskType: "COMPLETE_CUSTOM_TASK",
        title: "",
        description: "",
        emailsToNotify: [],
      };
      state.payment.tasks.unshift(newTask);

      // uncommnet the changes to add the add on specified step
      /*  // Find the last task for the specified step
      const lastTaskIndex = state.payment.tasks
        .map((task, index) => ({ task, index }))
        .filter(({ task }) => task.step === step)
        .map(({ index }) => index)
        .pop(); // Get the index of the last task with the same step

      if (lastTaskIndex !== undefined) {
        // Insert the new task right after the last task with the same step
        state.payment.tasks.splice(state.payment.tasks.length - 1, 0, newTask);
      } else {
        // If no tasks with the same step, add the task at the end
        state.payment.tasks.push(newTask);
      } */
    },
    removeCustomTask: (state, action) => {
      console.log(action.payload);
      state.payment.tasks = state.payment.tasks.filter((task) => task.id !== action.payload);
    },
    removeWorkOrderService: (state, action) => {
      try {
        const updatedValue = action.payload;

        // Filter the array to remove the service by ID
        state.summary.workExperience.workOrderServices = updatedValue;

        // Optionally log the updated list of services after removal
        console.log("Updated workOrderServices:", state.summary.workExperience.workOrderServices);
      } catch (error) {
        console.error("Error removing work order service:", error);
      }
    },

    updateTaskType: (state, action) => {
      const { id, newTask } = action.payload;

      // Find the task by ID and update its taskType
      const taskIndex = state.payment.tasks.findIndex((task) => task.id === id);

      if (taskIndex !== -1) {
        state.payment.tasks[taskIndex] = newTask;
      }
    },
    addContact: (state) => {
      const newContact = {
        id: Date.now(),
        name: "",
        position: "",
        phoneNumber: "",
        email: "",
        note: "",
      };
      state.scheduling.contacts.contacts.push(newContact);
    },
    updateContact: (state, action) => {
      const { index, field, value } = action.payload;
      state.scheduling.contacts.contacts[index][field] = value;
    },
    removeContact: (state, action) => {
      state.scheduling.contacts.contacts = state.scheduling.contacts.contacts.filter(
        (contact) => contact.id !== action.payload,
      );
    },
    removeCustomerFile: (state, action) => {
      state.summary.customerFiles.files.splice(action.payload, 1);
    },
    clearCustomerFiles: (state) => {
      state.summary.customerFiles = {
        files: [],
        isCustomerFilesVisible: false,
      };
    },
    removeDeliverableFileById: (state, action) => {
      const fileId = action.payload;
      state.summary.deliverables.files = state.summary.deliverables.files.filter(
        (file) => file.id !== fileId,
      );
    },
    addPurchaseOrder: (state) => {
      const newPO = {
        id: Date.now(),
        number: "",
        attachment: null,
      };
      state.payment.pay.purchaseOrders.push(newPO);
    },

    updatePurchaseOrder: (state, action) => {
      const { index, field, value } = action.payload;
      state.payment.pay.purchaseOrders[index][field] = value;
    },

    removePurchaseOrder: (state, action) => {
      state.payment.pay.purchaseOrders = state.payment.pay.purchaseOrders.filter(
        (po) => po.id !== action.payload,
      );
    },

    addExpense: (state) => {
      const newExpense = {
        id: Date.now(),
        expenseType: "",
        description: "",
        amount: "",
        expenseKey: crypto.randomUUID(),
        attachment: null,
      };
      state.payment.expenses.push(newExpense);
    },
    updateExpense: (state, action) => {
      const { index, field, value } = action.payload;
      state.payment.expenses[index][field] = value;
    },
    removeExpense: (state, action) => {
      state.payment.expenses = state.payment.expenses.filter(
        (expense) => expense.id !== action.payload,
      );
    },
    addEngineerExpense: (state) => {
      const newExpense = {
        id: Date.now(),
        expenseType: "",
        description: "",
        amount: "",
        expenseKey: crypto.randomUUID(),
        attachment: null,
      };
      state.payment.engineerExpenses.push(newExpense);
    },

    updateEngineerExpense: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.payment.engineerExpenses[index]) {
        state.payment.engineerExpenses[index][field] = value;
      }
    },

    removeEngineerExpense: (state, action) => {
      state.payment.engineerExpenses = state.payment.engineerExpenses.filter(
        (expense) => expense.id !== action.payload,
      );
    },

    addCompanyExpense: (state) => {
      const newExpense = {
        id: Date.now(),
        expenseType: "",
        description: "",
        amount: "",
        expenseKey: crypto.randomUUID(),
        attachment: null,
      };
      state.payment.companyExpenses.push(newExpense);
    },

    updateCompanyExpense: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.payment.companyExpenses[index]) {
        state.payment.companyExpenses[index][field] = value;
      }
    },

    removeCompanyExpense: (state, action) => {
      state.payment.companyExpenses = state.payment.companyExpenses.filter(
        (expense) => expense.id !== action.payload,
      );
    },

    addQualification: (state) => {
      const newQualification = {
        id: Date.now(),
        qualificationTypeId: null,
        qualificationSubTypes: [],
      };
      state.scheduling.qualifications.push(newQualification);
    },

    updateQualification: (state, action) => {
      const { index, field, value } = action.payload;
      if (state.scheduling.qualifications[index]) {
        // Reset sub-qualifications for this qualification
        state.scheduling.qualifications[index].qualificationSubTypes = [];
      }

      state.scheduling.qualifications[index][field] = value;
    },

    removeQualification: (state, action) => {
      const indexToRemove = action.payload;
      state.scheduling.qualifications.splice(indexToRemove, 1);
    },

    updateSubQualificationOptions: (state, action) => {
      const { qualificationTypeId, options } = action.payload;
      state.subQualificationOptions[qualificationTypeId] = options;
    },
    setWorkOrderDraftMessage: (state, action) => {
      const { workOrderId, userId, message, files, showEditor } = action.payload;

      if (!state.draftMessages[workOrderId]) {
        state.draftMessages[workOrderId] = {};
      }

      state.draftMessages[workOrderId][userId] = {
        ...state.draftMessages[workOrderId][userId],
        ...(message !== undefined ? { message } : {}),
        ...(files !== undefined ? { files } : {}),
        ...(showEditor !== undefined ? { showEditor } : {}),
      };
    },

    setWorkOrderChatEditorState: (state, action) => {
      const { workOrderId, userId, showEditor } = action.payload;

      if (!state.draftMessages[workOrderId]) {
        state.draftMessages[workOrderId] = {};
      }

      state.draftMessages[workOrderId][userId] = {
        ...state.draftMessages[workOrderId][userId],
        showEditor,
      };
    },

    clearWorkOrderDraftMessage: (state, action) => {
      const { workOrderId, userId } = action.payload;

      if (state.draftMessages[workOrderId] && state.draftMessages[workOrderId][userId]) {
        delete state.draftMessages[workOrderId][userId];

        if (Object.keys(state.draftMessages[workOrderId]).length === 0) {
          delete state.draftMessages[workOrderId];
        }
      }
    },
  },
});

export const {
  updateWorkOrderForm,
  resetPaymentFieldsAndValidation,
  resetEngineerPaymentFieldsAndValidation,
  setWorkOrderDetail,
  clearWorkOrderDetail,
  updateWorkOrderDetail,
  resetWorkOrderForm,
  addNotification,
  removeNotification,
  addEmailToNotify,
  updateTaskType,
  updateEmail,
  addCustomTask,
  taskHandleChange,
  addContact,
  updateContact,
  removeContact,
  addQualification,
  updateQualification,
  removeQualification,
  updateSubQualificationOptions,
  addPurchaseOrder,
  updatePurchaseOrder,
  removePurchaseOrder,
  addExpense,
  updateExpense,
  removeExpense,
  addEngineerExpense,
  updateEngineerExpense,
  removeEngineerExpense,
  addCompanyExpense,
  updateCompanyExpense,
  removeCompanyExpense,
  updateWorkOrderFormValidation,
  removeCustomTask,
  removeWorkOrderService,
  removeCustomerFile,
  clearCustomerFiles,
  removeDeliverableFileById,
  setWorkOrderDraftMessage,
  clearWorkOrderDraftMessage,
  setWorkOrderChatEditorState,
} = workOrderSlice.actions;
export default workOrderSlice.reducer;
